import { CircularProgress, makeStyles, Typography } from "@material-ui/core"
import React, { FC } from "react"
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryContainer,
  VictoryLegend,
  VictoryStack,
} from "victory"
import { colors } from "../../utils/theme"
import { ClientProgressData } from "../../utils/types"

const useStyles = makeStyles((theme) => ({
  clientProgress: {
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 4,
    padding: theme.spacing(3),
    minHeight: 332,
    display: "flex",
    flexDirection: "column",
  },
  titleSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(3),
    height: 24,
  },
  title: {
    fontWeight: 400,
    color: colors.neutral.denim,
  },
  last6Months: {
    color: colors.neutral.denim_08,
    fontStyle: "italic",
    fontWeight: 400,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
}))

const ChartColor = {
  IMPROVING: "rgba(117, 145, 228, 1)",
  STABLE: "rgba(117, 145, 228, 0.7)",
  DECLINING: "rgba(117, 145, 228, 0.25)",
}

const ClientProgress: FC<{ clientProgressData?: ClientProgressData }> = ({
  clientProgressData,
}) => {
  const classes = useStyles()
  return (
    <div className={classes.clientProgress}>
      <div className={classes.titleSection}>
        <Typography variant="h5" className={classes.title}>
          Client Progress
        </Typography>
        <Typography variant="body2" className={classes.last6Months}>
          all time
        </Typography>
      </div>
      {clientProgressData ? (
        <>
          <Typography variant="body2">Not Yet Available</Typography>
          <VictoryChart
            domainPadding={{ x: 32 }}
            height={332}
            width={410}
            padding={{ top: 8, bottom: 30, left: 36, right: 0 }}
          >
            <VictoryAxis
              orientation="top"
              tickComponent={<></>}
              tickLabelComponent={<></>}
              style={{
                axis: { stroke: colors.neutral.clay },
              }}
            />
            <VictoryAxis
              orientation="right"
              dependentAxis={true}
              tickComponent={<></>}
              tickLabelComponent={<></>}
              style={{
                axis: { stroke: colors.neutral.clay },
              }}
            />
            <VictoryAxis
              orientation="bottom"
              style={{
                tickLabels: {
                  fontFamily: "Lato",
                  color: colors.neutral.asphalt,
                  fontSize: 16,
                  fontWeight: 400,
                },
                axis: { stroke: colors.neutral.clay },
              }}
            />
            <VictoryAxis
              orientation="left"
              dependentAxis={true}
              tickValues={[0, 25, 50, 75, 100]}
              tickFormat={(tick) =>
                tick === 0 ? "00" : tick === 100 ? "100" : ""
              }
              style={{
                tickLabels: {
                  fontFamily: "Lato",
                  color: colors.neutral.asphalt,
                  fontSize: 12,
                  fontWeight: 400,
                },
                grid: { stroke: colors.neutral.clay },
                axis: { stroke: colors.neutral.clay },
              }}
            />
            <VictoryStack
              colorScale={[
                ChartColor.DECLINING,
                ChartColor.STABLE,
                ChartColor.IMPROVING,
              ]}
            >
              {clientProgressData?.map((bar, index) => (
                <VictoryBar key={index} data={bar} />
              ))}
            </VictoryStack>
          </VictoryChart>
          <VictoryLegend
            orientation="horizontal"
            standalone={true}
            containerComponent={
              <VictoryContainer
                responsive={false}
                style={{ top: 0, left: 32 }}
              />
            }
            gutter={32}
            height={25}
            style={{
              labels: {
                fontFamily: "Lato",
                color: colors.neutral.denim,
                fontSize: 14,
                fontWeight: 400,
              },
            }}
            data={[
              {
                name: "Improving",
                symbol: {
                  fill: ChartColor.IMPROVING,
                },
              },
              {
                name: "Stable",
                symbol: {
                  fill: ChartColor.STABLE,
                },
              },
              {
                name: "Declining",
                symbol: {
                  fill: ChartColor.DECLINING,
                },
              },
            ]}
          />
        </>
      ) : (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      )}
    </div>
  )
}

export default ClientProgress
